//@flow
import * as Immutable from 'immutable';

import { PjpRiderData } from 'common/models/flow/PjpRiderData';
import type { PjpRiderDataModelType } from 'common/models/flow/PjpRiderData';

import { ChannelData } from 'common/models/internal/ChannelData';

import { Revenue } from './Revenue';
import { SalesCategory } from './SalesCategory';
import { SalesCategoryId } from './SalesCategoryId';
import { Holiday } from './MasterOutlet';

import {
    validatable,
    validate,
    validateAll,
    validateZoneId,
    softValidateAll
} from 'common/utils/ValidationUtil';
import NumberUtils from 'common/utils/NumberUtils';
import DataUtils from 'common/utils/DataUtils';
import I18nHelper from 'common/helpers/I18nHelper';
const translationsNamespace = ['validations'];
const nsTranslate = I18nHelper.getTranslate(translationsNamespace);

@validatable
class InternalPjpRiderData extends PjpRiderData {
    static fromJS(json: PjpRiderDataModelType): InternalPjpRiderData {
        const state: Object = Object.assign({}, json);
        state.revenue = state.revenue
            ? Revenue.fromJS(state.revenue)
            : state.revenue;
        state.zoneIds = state.zoneIds
            ? Immutable.List(state.zoneIds)
            : state.zoneIds;
        state.holidays = state.holidays
            ? Immutable.List(state.holidays).map(item => Holiday.fromJS(item))
            : state.holidays;
        state.salesCategories = Immutable.List(
            state.salesCategories
        ).map(item => SalesCategory.fromJS(item));
        state.salesCategoryIds = state.salesCategoryIds
            ? Immutable.List(state.salesCategoryIds).map(item =>
                  SalesCategoryId.fromJS(item)
              )
            : state.salesCategoryIds;
        state.channels = Immutable.List(state.channels).map(item =>
            ChannelData.fromJS(item)
        );
        return new this(Immutable.Map(state));
    }

    @softValidateAll
    hasValidationErrors(): boolean {
        return false;
    }

    @validateAll
    setAllValidationErrors(): this {
        let { revenue } = this;
        if (revenue) {
            revenue = revenue.setAllValidationErrors();
        }
        return this.setRevenue(revenue);
    }

    @validate
    setRevenue(revenue: ?Revenue): this {
        return super.setRevenue(revenue);
    }

    validateRevenue() {
        return this.revenue && this.revenue.getValidationMessage('amount');
    }

    @validate
    setSkusSoldCount(skusSoldCount: ?number): this {
        return super.setSkusSoldCount(skusSoldCount);
    }

    @validate
    setSalesCategoryIds(salesCategoryIds: ?Array<SalesCategoryId>): this {
        return super.setSalesCategoryIds(salesCategoryIds);
    }

    @validate
    setZoneIds(zoneIds: ?Array<string>): this {
        return super.setZoneIds(zoneIds);
    }

    validateZoneIds() {
        const { zoneIds } = this;
        let error = '';
        let errorZones = [];
        if (zoneIds) {
            errorZones = zoneIds.find(zone => {
                const zoneError = validateZoneId(zone);
                if (zoneError) {
                    error = zoneError;
                    return zone;
                }
                return false;
            });
        }
        return errorZones?.length ? `${error} :${errorZones}` : null;
    }

    validateSalesCategoryIds() {
        const { salesCategoryIds } = this;
        const skipValidation = this.getShouldSkipSalesCategoryIdsValidation();
        // This is to bypass rider salesCategory field validation on change
        if (skipValidation) {
            return null;
        }
        if (salesCategoryIds?.size === 0) {
            return nsTranslate('salesCategoryEmpty');
        }
    }

    validateSkusSoldCount() {
        if (
            !DataUtils.isEmpty(this.skusSoldCount) &&
            !NumberUtils.isInteger(this.skusSoldCount)
        ) {
            return nsTranslate('skusSoldCountNotAnInteger');
        }
    }

    get revenue(): ?Revenue {
        return this._state.get('revenue');
    }

    get salesCategories(): Immutable.List<SalesCategory> {
        return this._state.get('salesCategories');
    }

    // Alternative to lazy loading of nested models (init and cleaning)
    defaultSanitize(): ?this {
        const sanitizedPjpRiderData = super.defaultSanitize();
        if (sanitizedPjpRiderData) {
            const sanitizedRevenue = sanitizedPjpRiderData.revenue?.defaultSanitize();
            return sanitizedPjpRiderData.setRevenue(sanitizedRevenue);
        }
        return sanitizedPjpRiderData;
    }

    static defaultInit({ props }): InternalPjpRiderData {
        const revenue = Revenue.defaultInit({
            props
        });
        return InternalPjpRiderData.fromJS({}).setRevenue(revenue);
    }

    get salesCategoryIds(): ?Immutable.List<SalesCategoryId> {
        return this._state.get('salesCategoryIds');
    }

    get channels(): Immutable.List<ChannelData> {
        return this._state.get('channels');
    }

    get holidays(): ?Immutable.List<Holiday> {
        return this._state.get('holidays');
    }

    setShouldSkipSalesCategoryIdsValidation(flag: boolean): this {
        return this.clone(
            this._state.set('shouldSkipSalesCategoryIdsValidation', flag)
        );
    }

    getShouldSkipSalesCategoryIdsValidation(): boolean {
        return this._state.get('shouldSkipSalesCategoryIdsValidation');
    }
}

export { InternalPjpRiderData as PjpRiderData };
